import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Building2, Copy, CreditCard, EyeOff, ImagePlus, Palette, Percent, Plus, QrCode, Save, Store,
  Trash2, Tv, Upload, X,
} from 'lucide-react';

import { AddressField } from '@/components/AddressField';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Toggle } from '@/components/ui/Toggle';
import {
  listEstablishments, createEstablishment, updateEstablishment, deleteEstablishment,
  listPaymentSettings, upsertPaymentSetting,
  getYukassaSettings, saveYukassaSettings, deleteYukassaSettings,
  uploadImage, listCategories, acquiringProviderLabels,
  type Establishment, type PaymentSetting,
  type AcquiringProvider, type AcquiringSettings, type MenuBoardSettings,
} from '@/services/admin';
import { paymentMethodLabels } from '@/utils/format';
import { asAbsoluteUrl } from '@/utils/url';
import { useEstablishmentStore } from '@/store/establishment.store';
import { invalidateEstablishmentScoped } from '@/utils/invalidate';

type Tab = 'general' | 'brand' | 'payments' | 'cashier';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'general', label: 'Заведение' },
  { id: 'brand', label: 'Меню и бренд' },
  { id: 'payments', label: 'Оплаты' },
  { id: 'cashier', label: 'Касса' },
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data: ests, isLoading } = useQuery({ queryKey: ['admin/ests'], queryFn: listEstablishments });
  const selectedId = useEstablishmentStore((s) => s.currentId);
  const setSelectedId = useEstablishmentStore((s) => s.setCurrentId);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<Tab>('general');

  useEffect(() => {
    if (!ests || ests.length === 0) return;
    const ids = new Set(ests.map((e) => e.id));
    if (!selectedId || !ids.has(selectedId)) setSelectedId(ests[0].id);
  }, [ests, selectedId, setSelectedId]);

  const selected = ests?.find((e) => e.id === selectedId) ?? null;

  const updateMut = useMutation({
    mutationFn: async (b: Partial<Establishment>) => {
      if (!selected) return;
      return updateEstablishment(selected.id, b);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin/ests'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteEstablishment,
    onSuccess: (_d, id) => {
      toast.success('Заведение удалено');
      qc.invalidateQueries({ queryKey: ['admin/ests'] });
      if (selectedId === id) setSelectedId(null);
      invalidateEstablishmentScoped(qc);
    },
  });

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold">Настройки</h1>
            <p className="text-sm text-slate-500">Заведение, бренд меню, оплаты и касса</p>
          </div>
          <button className="btn-outline shrink-0" onClick={() => setCreating(true)}>
            <Plus size={16} /> Новое заведение
          </button>
        </div>

        {ests && ests.length > 1 && (
          <select
            className="input mt-4 sm:max-w-xs"
            value={selectedId ?? ''}
            onChange={(e) => {
              setSelectedId(e.target.value);
              invalidateEstablishmentScoped(qc);
            }}
          >
            {ests.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
      </header>

      {isLoading && <Spinner />}

      {ests && ests.length === 0 && (
        <div className="card p-12 text-center text-slate-400">
          <Building2 size={28} className="mx-auto mb-2 opacity-40" />
          Ещё нет ни одного заведения. Создайте первое.
        </div>
      )}

      {selected && (
        <>
          <div className="mb-4 flex gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1 no-scrollbar">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition ${
                  tab === t.id ? 'bg-white text-brand-700 shadow' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'general' && (
            <GeneralTab
              key={selected.id}
              value={selected}
              onSave={(b) => updateMut.mutate(b)}
              pending={updateMut.isPending}
              onDelete={() => {
                if (confirm(`Удалить «${selected.name}»? Это необратимо.`)) delMut.mutate(selected.id);
              }}
            />
          )}
          {tab === 'brand' && (
            <BrandTab
              key={selected.id}
              value={selected}
              onSave={(b) => updateMut.mutate(b)}
              pending={updateMut.isPending}
            />
          )}
          {tab === 'payments' && (
            <PaymentsTab estId={selected.id} domain={selected.domain ?? null} />
          )}
          {tab === 'cashier' && (
            <CashierTab value={selected} onSave={(b) => updateMut.mutate(b)} pending={updateMut.isPending} />
          )}
        </>
      )}

      {creating && <CreateEstablishmentModal onClose={() => setCreating(false)} onCreated={(id) => setSelectedId(id)} />}
    </div>
  );
}

// ─── Строительные блоки страницы ──────────────────────────────────────
// Одна карточка на таб, внутри — секции через разделители. Так настройки
// читаются сверху вниз, а не рассыпаются мозаикой из карточек разной высоты.

function Section({ icon, title, description, children }: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="p-5">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 font-semibold">
          {icon}
          {title}
        </h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/** Строка «название + пояснение» с контролом справа. */
function SettingRow({ title, hint, children }: { title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/** Панель сохранения — появляется, только если что-то изменили. */
function SaveBar({ dirty, pending, onSave, onReset }: {
  dirty: boolean;
  pending: boolean;
  onSave: () => void;
  onReset: () => void;
}) {
  if (!dirty) return null;
  return (
    <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-slate-100 bg-white/95 px-5 py-3 backdrop-blur">
      <span className="text-xs text-slate-500">Есть несохранённые изменения</span>
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onReset} disabled={pending}>Отменить</button>
        <button className="btn-primary" onClick={onSave} disabled={pending}>
          <Save size={16} /> {pending ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}

// ─── Таб «Заведение» ──────────────────────────────────────────────────
function GeneralTab({ value, onSave, pending, onDelete }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
  onDelete: () => void;
}) {
  const [v, setV] = useState<Establishment>(value);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setV(value); }, [value]);

  const dirty = useMemo(
    () => (
      v.name !== value.name
      || (v.address ?? '') !== (value.address ?? '')
      || (v.phone ?? '') !== (value.phone ?? '')
      || (v.city ?? '') !== (value.city ?? '')
      || v.geoLat !== value.geoLat
      || v.geoLon !== value.geoLon
      || v.showOnPublicMap !== value.showOnPublicMap
      || v.timezone !== value.timezone
      || v.currency !== value.currency
      || (v.logoUrl ?? '') !== (value.logoUrl ?? '')
    ),
    [v, value],
  );

  const onPickLogo = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadImage('logo', f);
      setV((x) => ({ ...x, logoUrl: res.url }));
    } catch {
      /* обработано interceptor-ом */
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card overflow-hidden divide-y divide-slate-100">
      <Section icon={<Building2 size={18} />} title="Основное">
        <div className="mb-5 flex items-center gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            {v.logoUrl ? (
              <img src={asAbsoluteUrl(v.logoUrl) ?? undefined} alt="Логотип" className="h-full w-full object-contain" />
            ) : (
              <ImagePlus size={24} className="text-slate-300" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium">Логотип</div>
            <div className="mt-0.5 text-xs text-slate-500">PNG, JPG, WebP или SVG, до 5 МБ</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              hidden
              onChange={(e) => onPickLogo(e.target.files?.[0] ?? null)}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <button className="btn-outline" disabled={uploading} onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> {uploading ? 'Загрузка…' : 'Загрузить'}
              </button>
              {v.logoUrl && (
                <button className="btn-ghost text-rose-600" onClick={() => setV({ ...v, logoUrl: null })}>
                  <X size={14} /> Убрать
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          <div>
            <label className="label">Название</label>
            <input className="input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Адрес</label>
            <AddressField
              value={v.address ?? ''}
              city={v.city}
              hasCoords={v.geoLat != null && v.geoLon != null}
              onChange={({ address, city, lat, lon }) =>
                setV({ ...v, address, city, geoLat: lat, geoLon: lon })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="label">Телефон</label>
              <input className="input" value={v.phone ?? ''} onChange={(e) => setV({ ...v, phone: e.target.value })} />
            </div>
            <div>
              <label className="label">Валюта</label>
              <input className="input" value={v.currency} onChange={(e) => setV({ ...v, currency: e.target.value })} />
            </div>
            <div>
              <label className="label">Таймзона</label>
              <input className="input" value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })} />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Видимость">
        <SettingRow
          title="Показывать на карте resstro.ru"
          hint="Название, логотип, адрес и телефон увидят посетители сайта"
        >
          <Toggle
            checked={v.showOnPublicMap}
            onChange={(checked) => setV({ ...v, showOnPublicMap: checked })}
            label="Показывать на карте resstro.ru"
          />
        </SettingRow>
      </Section>

      <SaveBar
        dirty={dirty}
        pending={pending}
        onReset={() => setV(value)}
        onSave={() => onSave({
          name: v.name,
          address: v.address,
          phone: v.phone,
          city: v.city,
          geoLat: v.geoLat,
          geoLon: v.geoLon,
          showOnPublicMap: v.showOnPublicMap,
          timezone: v.timezone,
          currency: v.currency,
          logoUrl: v.logoUrl,
        })}
      />

      <Section title="Удаление">
        <SettingRow
          title="Удалить заведение"
          hint="Вместе с меню, складом, сменами и заказами. Отменить нельзя."
        >
          <button className="btn-outline text-rose-600" onClick={onDelete}>
            <Trash2 size={16} /> Удалить
          </button>
        </SettingRow>
      </Section>
    </div>
  );
}

// ─── Таб «Меню и бренд» ───────────────────────────────────────────────
type EstablishmentThemeValue = {
  primary?: string | null;
  fontFamily?: string | null;
  faviconUrl?: string | null;
  iconUrl?: string | null;
};

const FONT_PRESETS: Array<{ label: string; value: string }> = [
  { label: 'Inter (по умолчанию)', value: 'Inter' },
  { label: 'Manrope', value: 'Manrope' },
  { label: 'Rubik', value: 'Rubik' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Playfair Display (с засечками)', value: '"Playfair Display"' },
  { label: 'IBM Plex Sans', value: '"IBM Plex Sans"' },
];

function BrandTab({ value, onSave, pending }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
}) {
  const initialTheme = (value.theme ?? {}) as EstablishmentThemeValue;
  const [domain, setDomain] = useState(value.domain ?? '');
  const [theme, setTheme] = useState<EstablishmentThemeValue>(initialTheme);
  const [upFav, setUpFav] = useState(false);
  const [upIcon, setUpIcon] = useState(false);
  const favRef = useRef<HTMLInputElement>(null);
  const iconRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDomain(value.domain ?? '');
    setTheme((value.theme ?? {}) as EstablishmentThemeValue);
  }, [value]);

  const primary = theme.primary ?? '#4f62e0';
  const fontFamily = theme.fontFamily ?? 'Inter';
  const faviconUrl = theme.faviconUrl ?? null;
  const iconUrl = theme.iconUrl ?? null;

  const dirty = useMemo(
    () => (
      domain.trim() !== (value.domain ?? '')
      || (theme.primary ?? '') !== (initialTheme.primary ?? '')
      || (theme.fontFamily ?? '') !== (initialTheme.fontFamily ?? '')
      || (theme.faviconUrl ?? '') !== (initialTheme.faviconUrl ?? '')
      || (theme.iconUrl ?? '') !== (initialTheme.iconUrl ?? '')
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domain, theme, value],
  );

  const pick = async (kind: 'favicon' | 'icon', f: File | null) => {
    if (!f) return;
    if (kind === 'favicon') setUpFav(true);
    else setUpIcon(true);
    try {
      const res = await uploadImage('icon', f);
      setTheme((t) => (kind === 'favicon' ? { ...t, faviconUrl: res.url } : { ...t, iconUrl: res.url }));
    } catch {
      /* интерцептор покажет */
    } finally {
      if (kind === 'favicon') setUpFav(false);
      else setUpIcon(false);
    }
  };

  return (
    <div className="card overflow-hidden divide-y divide-slate-100">
      <Section
        icon={<Palette size={18} />}
        title="Как выглядит меню"
        description="Акцентный цвет и шрифт применяются к меню гостя. Остальные оттенки подбираются автоматически."
      >
        <div
          className="mb-5 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4"
          style={{ fontFamily: `${fontFamily}, Inter, system-ui, sans-serif` }}
        >
          <div className="h-10 w-10 rounded-xl" style={{ background: primary, boxShadow: `0 2px 10px ${primary}33` }} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Превью шрифта и цвета</div>
            <div className="text-xs text-slate-500">Так гость увидит акцент в меню</div>
          </div>
          <button type="button" className="rounded-xl px-3 py-2 text-sm text-white" style={{ background: primary }}>
            В корзину
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Основной цвет</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={primary}
                onChange={(e) => setTheme({ ...theme, primary: e.target.value })}
                className="h-10 w-12 cursor-pointer rounded-lg border border-slate-200 p-0"
              />
              <input
                className="input"
                value={primary}
                onChange={(e) => setTheme({ ...theme, primary: e.target.value })}
                placeholder="#4f62e0"
              />
            </div>
          </div>
          <div>
            <label className="label">Шрифт</label>
            <select
              className="input"
              value={fontFamily}
              onChange={(e) => setTheme({ ...theme, fontFamily: e.target.value })}
            >
              {FONT_PRESETS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <IconPicker
            label="Favicon (вкладка браузера)"
            hint="Квадратное, 256×256"
            url={faviconUrl}
            rounded="rounded-lg"
            uploading={upFav}
            inputRef={favRef}
            onPick={(f) => pick('favicon', f)}
            onClear={() => setTheme({ ...theme, faviconUrl: null })}
          />
          <IconPicker
            label="Иконка приложения"
            hint="Квадратная, от 512×512"
            url={iconUrl}
            rounded="rounded-2xl"
            uploading={upIcon}
            inputRef={iconRef}
            onPick={(f) => pick('icon', f)}
            onClear={() => setTheme({ ...theme, iconUrl: null })}
          />
        </div>
      </Section>

      <Section
        title="Свой домен меню"
        description="При заходе по этому адресу открывается меню вашего заведения. HTTPS-сертификат выпускается автоматически."
      >
        <DomainField value={domain} onChange={setDomain} />
      </Section>

      <SaveBar
        dirty={dirty}
        pending={pending}
        onReset={() => {
          setDomain(value.domain ?? '');
          setTheme((value.theme ?? {}) as EstablishmentThemeValue);
        }}
        onSave={() => onSave({
          domain: domain.trim() || null,
          theme: {
            // Пустая строка = «оставить стандартное» (бэк сбросит в дефолт).
            // null сюда слать нельзя — zod на бэке ждёт строку.
            primary: theme.primary ?? '',
            fontFamily: theme.fontFamily ?? '',
            faviconUrl: theme.faviconUrl ?? null,
            iconUrl: theme.iconUrl ?? null,
          },
        })}
      />
    </div>
  );
}

function IconPicker({ label, hint, url, rounded, uploading, inputRef, onPick, onClear }: {
  label: string;
  hint: string;
  url: string | null;
  rounded: string;
  uploading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (f: File | null) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden border border-slate-200 bg-slate-50 ${rounded}`}>
        {url ? (
          <img src={asAbsoluteUrl(url) ?? undefined} alt={label} className="h-full w-full object-contain" />
        ) : (
          <ImagePlus size={20} className="text-slate-300" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="mt-0.5 text-xs text-slate-500">{hint}</div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <button className="btn-outline" disabled={uploading} onClick={() => inputRef.current?.click()}>
            <Upload size={14} /> {uploading ? 'Загрузка…' : 'Загрузить'}
          </button>
          {url && (
            <button className="btn-ghost text-rose-600" onClick={onClear}>
              <X size={14} /> Убрать
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Таб «Оплаты» ─────────────────────────────────────────────────────
// Способы оплаты в кассе и реквизиты онлайн-оплаты гостем — в одном месте:
// это один и тот же вопрос «чем платит гость».
const CASHIER_METHODS = ['CASH', 'CARD', 'TRANSFER', 'QRCODE', 'MIXED'];

function PaymentsTab({ estId, domain }: { estId: string; domain: string | null }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin/payments', estId],
    queryFn: () => listPaymentSettings(estId),
  });

  const upsertMut = useMutation({
    mutationFn: async (b: Partial<PaymentSetting>) => upsertPaymentSetting(estId, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin/payments', estId] }),
  });

  const online = data?.find((x) => x.method === 'ONLINE');
  const qr = data?.find((x) => x.method === 'QRCODE');

  return (
    <div className="card overflow-hidden divide-y divide-slate-100">
      <Section
        icon={<QrCode size={18} />}
        title="Способы оплаты в кассе"
        description="Кассир увидит только включённые способы при закрытии заказа."
      >
        {isLoading ? <Spinner /> : (
          <div className="divide-y divide-slate-100">
            {CASHIER_METHODS.map((m) => {
              const p = data?.find((x) => x.method === m);
              return (
                <SettingRow key={m} title={paymentMethodLabels[m] ?? m}>
                  <Toggle
                    checked={p?.isEnabled ?? false}
                    onChange={(checked) => upsertMut.mutate({
                      method: m,
                      isEnabled: checked,
                      qrImageUrl: p?.qrImageUrl ?? null,
                    })}
                    label={paymentMethodLabels[m] ?? m}
                  />
                </SettingRow>
              );
            })}
          </div>
        )}

        {qr?.isEnabled && (
          <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div className="mb-2 text-sm font-medium">QR-код для перевода</div>
            <QrPicker
              current={qr.qrImageUrl ?? null}
              onChange={(url) => upsertMut.mutate({ method: 'QRCODE', isEnabled: true, qrImageUrl: url })}
            />
          </div>
        )}
      </Section>

      <Section
        icon={<CreditCard size={18} />}
        title="Онлайн-оплата гостем"
        description="Гость оплачивает заказ прямо в меню. Деньги приходят на ваш магазин ЮKassa, не через платформу."
      >
        <div className="mb-4 divide-y divide-slate-100">
          <SettingRow
            title="Принимать оплату из меню"
            hint="Без реквизитов ниже кнопка оплаты гостю не покажется"
          >
            <Toggle
              checked={online?.isEnabled ?? false}
              onChange={(checked) => upsertMut.mutate({
                method: 'ONLINE',
                isEnabled: checked,
                qrImageUrl: online?.qrImageUrl ?? null,
              })}
              label="Принимать оплату из меню"
            />
          </SettingRow>
        </div>
        <YukassaBlock estId={estId} domain={domain} />
      </Section>
    </div>
  );
}

// Секрет уходит на сервер один раз и обратно не приходит: здесь видна
// только маска последних символов.
function YukassaBlock({ estId, domain }: { estId: string; domain: string | null }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin/yukassa', estId],
    queryFn: () => getYukassaSettings(estId),
  });
  const [shopId, setShopId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [editing, setEditing] = useState(false);

  const saveMut = useMutation({
    mutationFn: () => saveYukassaSettings(estId, { shopId: shopId.trim(), secretKey: secretKey.trim() }),
    onSuccess: () => {
      toast.success('Реквизиты ЮKassa проверены и сохранены');
      setSecretKey('');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['admin/yukassa', estId] });
    },
  });

  const delMut = useMutation({
    mutationFn: () => deleteYukassaSettings(estId),
    onSuccess: () => {
      toast.success('Реквизиты удалены');
      setShopId('');
      setSecretKey('');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['admin/yukassa', estId] });
    },
  });

  if (isLoading) return <Spinner />;

  const apiOrigin = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
  const webhookUrl = `${apiOrigin}/api/client/webhooks/yukassa`;
  const menuOrigin = domain ? `https://${domain}` : null;
  const showForm = editing || !data?.isConfigured;
  const canSave = !!data?.canConfigure && shopId.trim().length > 0 && secretKey.trim().length > 0;

  return (
    <div className="rounded-xl border border-slate-100 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {data?.isConfigured ? <Badge tone="success">Реквизиты подключены</Badge> : <Badge tone="danger">Реквизиты не заданы</Badge>}
        {data?.mode === 'test' && <Badge tone="warning">Тестовый ключ</Badge>}
      </div>

      {data?.isConfigured && (
        <dl className="mb-3 grid gap-1 text-sm text-slate-600">
          <div className="flex gap-2"><dt className="text-slate-400">Магазин</dt><dd className="font-medium">{data.shopId}</dd></div>
          <div className="flex gap-2"><dt className="text-slate-400">Ключ</dt><dd className="font-medium">{data.secretKeyMask}</dd></div>
          {data.updatedAt && (
            <div className="text-xs text-slate-400">Обновлён {new Date(data.updatedAt).toLocaleString('ru-RU')}</div>
          )}
        </dl>
      )}

      {data?.mode === 'test' && (
        <p className="mb-3 text-xs text-amber-600">
          Тестовый ключ (test_…) реальные деньги не принимает — для приёма оплат укажите боевой.
        </p>
      )}
      {!data?.isConfigured && data?.platformFallback && (
        <p className="mb-3 text-xs text-amber-600">
          Пока оплаты идут через общий магазин платформы. Укажите свой ключ, чтобы деньги приходили вам напрямую.
        </p>
      )}
      {!data?.isConfigured && !data?.platformFallback && (
        <p className="mb-3 text-xs text-rose-600">
          Онлайн-оплата недоступна: при попытке оплатить гость увидит предложение оплатить на месте.
        </p>
      )}
      {data && !data.canConfigure && (
        <p className="mb-3 text-xs text-rose-600">
          Сохранение отключено: на сервере не задан ключ шифрования секретов. Обратитесь в поддержку Resstro.
        </p>
      )}

      {showForm ? (
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">shopId (номер магазина)</label>
              <input
                className="input"
                inputMode="numeric"
                placeholder="123456"
                value={shopId}
                disabled={!data?.canConfigure || saveMut.isPending}
                onChange={(e) => setShopId(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Секретный ключ</label>
              <input
                className="input"
                type="password"
                autoComplete="off"
                placeholder="live_…"
                value={secretKey}
                disabled={!data?.canConfigure || saveMut.isPending}
                onChange={(e) => setSecretKey(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-primary" disabled={!canSave || saveMut.isPending} onClick={() => saveMut.mutate()}>
              <Save size={16} /> {saveMut.isPending ? 'Проверяем в ЮKassa…' : 'Сохранить'}
            </button>
            {data?.isConfigured && (
              <button className="btn-ghost" onClick={() => { setEditing(false); setSecretKey(''); }}>Отмена</button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-outline" onClick={() => { setShopId(data?.shopId ?? ''); setEditing(true); }}>
            Заменить ключ
          </button>
          <button
            className="btn-ghost text-rose-600"
            disabled={delMut.isPending}
            onClick={() => {
              if (confirm('Удалить реквизиты? Онлайн-оплата в меню перестанет работать.')) delMut.mutate();
            }}
          >
            <Trash2 size={16} /> Удалить
          </button>
        </div>
      )}

      <div className="mt-4 space-y-1 text-xs text-slate-400">
        <div>Ключи: личный кабинет ЮKassa → «Интеграция» → «Ключи API».</div>
        <div>
          Там же включите уведомления <b>payment.succeeded</b> на адрес:{' '}
          <code className="break-all text-slate-500">{webhookUrl}</code>
        </div>
        {menuOrigin && <div>После оплаты гость вернётся на <b className="text-slate-500">{menuOrigin}</b>.</div>}
      </div>
    </div>
  );
}

function QrPicker({ current, onChange }: { current: string | null; onChange: (url: string | null) => void }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const pick = async (f: File | null) => {
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadImage('qr', f);
      onChange(res.url);
      toast.success('QR-картинка обновлена');
    } catch { /* интерцепторы */ }
    finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
        {current ? (
          <img src={asAbsoluteUrl(current) ?? undefined} alt="QR" className="h-full w-full object-contain" />
        ) : (
          <QrCode size={20} className="text-slate-300" />
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        hidden
        onChange={(e) => pick(e.target.files?.[0] ?? null)}
      />
      <div className="flex flex-wrap gap-2">
        <button className="btn-outline" disabled={uploading} onClick={() => ref.current?.click()}>
          <Upload size={14} /> {uploading ? 'Загрузка…' : current ? 'Заменить' : 'Загрузить QR'}
        </button>
        {current && (
          <button className="btn-ghost text-rose-600" onClick={() => onChange(null)}>
            <X size={14} /> Убрать
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Таб «Касса» ──────────────────────────────────────────────────────
function CashierTab({ value, onSave, pending }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
}) {
  // Магазин «Закуп» включён по умолчанию — выключение хранится явным false.
  const storeEnabled = value.settings?.supplierStore?.enabled ?? true;
  const revenueHidden = value.settings?.hideRevenueFromCashier ?? false;

  return (
    <>
    <div className="card overflow-hidden divide-y divide-slate-100">
      <CashierDiscountSection value={value} onSave={onSave} pending={pending} />
      <AcquiringSection value={value} onSave={onSave} pending={pending} />
      <MenuBoardSection value={value} onSave={onSave} pending={pending} />

      <Section
        icon={<Store size={18} />}
        title="Магазин «Закуп»"
        description="B2B-витрина поставщиков в кассе: заказ товаров и приёмка на склад одним нажатием. Для заведения бесплатно."
      >
        <SettingRow
          title={storeEnabled ? 'Магазин включён' : 'Магазин выключен'}
          hint={storeEnabled
            ? 'Кассиры видят витрину поставщиков и могут заказывать'
            : 'В кассе доступны только ручные поставки'}
        >
          <Toggle
            checked={storeEnabled}
            disabled={pending}
            onChange={(checked) => onSave({ supplierStore: { enabled: checked } })}
            label="Магазин «Закуп»"
          />
        </SettingRow>
      </Section>

      <Section
        icon={<EyeOff size={18} />}
        title="Выручка в кассе"
        description="Владелец и администратор под своей учётной записью видят оборот всегда. Применяется при следующем входе кассира."
      >
        <SettingRow
          title={revenueHidden ? 'Выручка скрыта от кассиров' : 'Выручка видна кассирам'}
          hint={revenueHidden
            ? 'Кассиры не видят итоги смены и оборот заведения'
            : 'Кассиры видят итоги смены и выручку в кассе'}
        >
          <Toggle
            checked={revenueHidden}
            disabled={pending}
            onChange={(checked) => onSave({ hideRevenueFromCashier: checked })}
            label="Скрыть выручку от кассиров"
          />
        </SettingRow>
      </Section>
    </div>
    <p className="mt-3 px-1 text-xs text-slate-400">
      Принтеры настраиваются в самом кассовом приложении — оно находит их в локальной сети
      и по Bluetooth: Настройки → Принтеры на планшете или Windows-кассе.
    </p>
    </>
  );
}

// ─── Касса: лимит скидки кассира (п.6) ────────────────────────────────
// Один лимит на заведение вместо поля у каждого пользователя. Пусто → касса
// берёт старое значение с пользователя (обратная совместимость).
function CashierDiscountSection({ value, onSave, pending }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
}) {
  const saved = value.settings?.cashierMaxDiscountPercent ?? null;
  const [draft, setDraft] = useState<string>(saved === null ? '' : String(saved));
  useEffect(() => { setDraft(saved === null ? '' : String(saved)); }, [saved, value.id]);

  const parsed = draft.trim() === '' ? null : Number(draft);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 0 && parsed <= 100);
  const dirty = parsed !== saved;

  return (
    <Section
      icon={<Percent size={18} />}
      title="Скидка кассира"
      description="Максимальная скидка, которую кассир может дать на заказ без администратора. Действует для всех кассиров заведения; применяется при следующем входе в кассу."
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="label">Лимит, %</span>
          <input
            className={`input w-32 ${valid ? '' : 'border-red-400'}`}
            inputMode="numeric"
            placeholder="как у пользователя"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
          />
        </label>
        {dirty && (
          <button
            className="btn-primary"
            disabled={pending || !valid}
            onClick={() => onSave({ cashierMaxDiscountPercent: parsed })}
          >
            <Save size={16} /> Сохранить
          </button>
        )}
        {saved !== null && !dirty && (
          <button className="btn-ghost text-slate-500" disabled={pending} onClick={() => onSave({ cashierMaxDiscountPercent: null })}>
            Сбросить (брать с пользователя)
          </button>
        )}
      </div>
      {!valid && <p className="mt-1 text-xs text-red-500">Целое число от 0 до 100</p>}
      <p className="mt-2 text-xs text-slate-400">
        0 — кассир не может давать скидки вообще. Скидки выше лимита оформляет администратор в админке.
      </p>
    </Section>
  );
}

// ─── Касса: банковский терминал (док 28-ekvayring.md) ──────────────────
const ACQUIRING_PROVIDERS: AcquiringProvider[] = ['none', 'manual', 'inpas', 'sber', 'arcus', 'sbp_qr'];
const NETWORK_PROVIDERS = new Set<AcquiringProvider>(['inpas', 'sber', 'arcus']);

function AcquiringSection({ value, onSave, pending }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
}) {
  const saved = useMemo<AcquiringSettings>(() => ({
    provider: value.settings?.acquiring?.provider ?? 'manual',
    host: value.settings?.acquiring?.host ?? '',
    port: value.settings?.acquiring?.port ?? null,
    terminalId: value.settings?.acquiring?.terminalId ?? '',
    timeoutSec: value.settings?.acquiring?.timeoutSec ?? 90,
    printSlip: value.settings?.acquiring?.printSlip ?? true,
  }), [value.settings?.acquiring]);
  const [draft, setDraft] = useState<AcquiringSettings>(saved);
  useEffect(() => { setDraft(saved); }, [saved]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const needsNetwork = NETWORK_PROVIDERS.has(draft.provider);
  const hostOk = !needsNetwork || (draft.host ?? '').trim().length > 0;
  const portOk = draft.port === null || draft.port === undefined
    || (Number.isInteger(draft.port) && draft.port >= 1 && draft.port <= 65535);
  const timeoutOk = Number.isInteger(draft.timeoutSec) && (draft.timeoutSec ?? 0) >= 10 && (draft.timeoutSec ?? 0) <= 600;
  const valid = hostOk && portOk && timeoutOk;

  const set = <K extends keyof AcquiringSettings>(k: K, v: AcquiringSettings[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Section
      icon={<CreditCard size={18} />}
      title="Банковский терминал"
      description="Как касса общается с эквайринговым терминалом при оплате «Карта». Настройка общая для всех касс заведения; параметры подключения касса подхватывает при следующем входе."
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="label">Режим</span>
          <select
            className="input"
            value={draft.provider}
            onChange={(e) => set('provider', e.target.value as AcquiringProvider)}
          >
            {ACQUIRING_PROVIDERS.map((p) => (
              <option key={p} value={p}>{acquiringProviderLabels[p]}</option>
            ))}
          </select>
        </label>

        {needsNetwork && (
          <>
            <label className="block">
              <span className="label">Адрес терминала / сервиса</span>
              <input
                className={`input ${hostOk ? '' : 'border-red-400'}`}
                placeholder={draft.provider === 'inpas' ? '127.0.0.1 (Inpas DC на кассе)' : '192.168.1.50'}
                value={draft.host ?? ''}
                onChange={(e) => set('host', e.target.value.trim())}
              />
            </label>
            <label className="block">
              <span className="label">Порт</span>
              <input
                className={`input ${portOk ? '' : 'border-red-400'}`}
                inputMode="numeric"
                placeholder={draft.provider === 'inpas' ? '8888' : draft.provider === 'sber' ? '8888' : '—'}
                value={draft.port ?? ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, '');
                  set('port', raw === '' ? null : Number(raw));
                }}
              />
            </label>
            <label className="block">
              <span className="label">ID терминала (TID)</span>
              <input
                className="input"
                placeholder="необязательно"
                value={draft.terminalId ?? ''}
                onChange={(e) => set('terminalId', e.target.value.trim())}
              />
            </label>
            <label className="block">
              <span className="label">Таймаут операции, сек</span>
              <input
                className={`input ${timeoutOk ? '' : 'border-red-400'}`}
                inputMode="numeric"
                value={draft.timeoutSec ?? ''}
                onChange={(e) => set('timeoutSec', Number(e.target.value.replace(/[^\d]/g, '') || 0))}
              />
            </label>
          </>
        )}
      </div>

      {draft.provider !== 'none' && (
        <div className="mt-3">
          <SettingRow
            title="Печатать слип на чековом принтере"
            hint="Для терминалов без своего принтера: касса печатает банковский слип вместе с чеком"
          >
            <Toggle
              checked={draft.printSlip ?? true}
              disabled={pending}
              onChange={(v) => set('printSlip', v)}
              label="Печатать слип"
            />
          </SettingRow>
        </div>
      )}

      <p className="mt-2 text-xs text-slate-400">
        {draft.provider === 'manual' && 'Кассир проводит оплату на терминале руками, касса лишь фиксирует «Карта». Так работает сейчас.'}
        {draft.provider === 'none' && 'Кнопки «Карта» в кассе не будет — только наличные, перевод и QR.'}
        {draft.provider === 'inpas' && 'Inpas Dual Connector: Т-Банк, Альфа, Райффайзен, ВТБ, ПСБ. Сервис DC ставится на кассу (Windows/Android) — обычно 127.0.0.1:8888.'}
        {draft.provider === 'sber' && 'Сбер UPOS / TTK 2.x по TCP: терминал должен быть в той же сети, что касса.'}
        {draft.provider === 'arcus' && 'Arcus 2: Газпромбанк, Уралсиб, Открытие. Требуется библиотека Arcus на кассе.'}
        {draft.provider === 'sbp_qr' && 'Оплата по QR СБП без терминала: касса показывает QR, банк подтверждает платёж.'}
      </p>

      {dirty && (
        <div className="mt-3 flex gap-2">
          <button className="btn-ghost" disabled={pending} onClick={() => setDraft(saved)}>Отменить</button>
          <button className="btn-primary" disabled={pending || !valid} onClick={() => onSave({ acquiring: draft })}>
            <Save size={16} /> Сохранить
          </button>
        </div>
      )}
    </Section>
  );
}

// ─── Касса: меню на телевизор (п.15) ───────────────────────────────────
function MenuBoardSection({ value, onSave, pending }: {
  value: Establishment;
  onSave: (b: Partial<Establishment>) => void;
  pending: boolean;
}) {
  const saved = useMemo<MenuBoardSettings>(() => ({
    enabled: value.settings?.menuBoard?.enabled ?? false,
    categoryIds: value.settings?.menuBoard?.categoryIds ?? [],
    rotateSec: value.settings?.menuBoard?.rotateSec ?? 12,
    showPhotos: value.settings?.menuBoard?.showPhotos ?? true,
    showPrices: value.settings?.menuBoard?.showPrices ?? true,
    title: value.settings?.menuBoard?.title ?? '',
  }), [value.settings?.menuBoard]);
  const [draft, setDraft] = useState<MenuBoardSettings>(saved);
  useEffect(() => { setDraft(saved); }, [saved]);

  const { data: categories } = useQuery({
    queryKey: ['admin/categories', value.id],
    queryFn: listCategories,
    enabled: draft.enabled,
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const rotateOk = Number.isInteger(draft.rotateSec) && (draft.rotateSec ?? 0) >= 5 && (draft.rotateSec ?? 0) <= 300;
  const set = <K extends keyof MenuBoardSettings>(k: K, v: MenuBoardSettings[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const toggleCat = (id: string) =>
    setDraft((d) => {
      const cur = d.categoryIds ?? [];
      return { ...d, categoryIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });

  const url = value.menuBoardUrl;

  return (
    <Section
      icon={<Tv size={18} />}
      title="Меню на телевизор"
      description="Публичная страница с меню для экрана в зале: открывается в браузере ТВ или Smart-TV-приставке, обновляется сама при изменении меню и стоп-листа."
    >
      <SettingRow
        title={saved.enabled ? 'Менюборд включён' : 'Менюборд выключен'}
        hint={saved.enabled ? 'Страница доступна по ссылке ниже' : 'Ссылка отдаёт «менюборд выключен»'}
      >
        <Toggle
          checked={draft.enabled ?? false}
          disabled={pending}
          onChange={(v) => {
            const next = { ...draft, enabled: v };
            setDraft(next);
            // Тумблер — как остальные в табе: сохраняем сразу.
            onSave({ menuBoard: next });
          }}
          label="Менюборд"
        />
      </SettingRow>

      {draft.enabled && (
        <div className="mt-3 space-y-3">
          {url && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <a href={url} target="_blank" rel="noreferrer" className="break-all font-mono text-xs text-brand-700 hover:underline">
                {url}
              </a>
              <button
                className="btn-ghost text-xs"
                onClick={() => {
                  void navigator.clipboard?.writeText(url).then(
                    () => toast.success('Ссылка скопирована'),
                    () => toast.error('Не удалось скопировать'),
                  );
                }}
              >
                <Copy size={14} /> Скопировать
              </button>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label">Заголовок на экране</span>
              <input
                className="input"
                placeholder={value.name}
                maxLength={60}
                value={draft.title ?? ''}
                onChange={(e) => set('title', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label">Смена категории, сек</span>
              <input
                className={`input ${rotateOk ? '' : 'border-red-400'}`}
                inputMode="numeric"
                value={draft.rotateSec ?? ''}
                onChange={(e) => set('rotateSec', Number(e.target.value.replace(/[^\d]/g, '') || 0))}
              />
              {!rotateOk && <span className="mt-1 block text-xs text-red-500">От 5 до 300 секунд</span>}
            </label>
          </div>

          <SettingRow title="Показывать фото блюд" hint="Без фото на экран помещается больше позиций">
            <Toggle checked={draft.showPhotos ?? true} disabled={pending} onChange={(v) => set('showPhotos', v)} label="Фото" />
          </SettingRow>
          <SettingRow title="Показывать цены" hint="Выключите, если экран используется как витрина без прайса">
            <Toggle checked={draft.showPrices ?? true} disabled={pending} onChange={(v) => set('showPrices', v)} label="Цены" />
          </SettingRow>

          <div>
            <div className="label">Категории на экране</div>
            <p className="mb-2 text-xs text-slate-400">Ничего не выбрано — показываются все активные категории меню.</p>
            {!categories ? (
              <Spinner />
            ) : categories.length === 0 ? (
              <p className="text-sm text-slate-500">Категорий пока нет — создайте их в разделе «Меню».</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {categories.filter((c) => c.isActive).map((c) => {
                  const on = (draft.categoryIds ?? []).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCat(c.id)}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        on
                          ? 'border-brand-600 bg-brand-50 text-brand-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {dirty && (
            <div className="flex gap-2">
              <button className="btn-ghost" disabled={pending} onClick={() => setDraft(saved)}>Отменить</button>
              <button className="btn-primary" disabled={pending || !rotateOk} onClick={() => onSave({ menuBoard: draft })}>
                <Save size={16} /> Сохранить
              </button>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

// ─── Новое заведение ──────────────────────────────────────────────────
function CreateEstablishmentModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [v, setV] = useState({
    name: '',
    address: '',
    // Заполняются выбором подсказки адреса — нужны для карты на лендинге.
    city: null as string | null,
    geoLat: null as number | null,
    geoLon: null as number | null,
    phone: '',
    domain: '',
    timezone: 'Europe/Moscow',
    currency: 'RUB',
  });

  const mut = useMutation({
    mutationFn: () => createEstablishment({
      name: v.name.trim(),
      address: v.address.trim() || undefined,
      city: v.city,
      geoLat: v.geoLat,
      geoLon: v.geoLon,
      phone: v.phone.trim() || undefined,
      domain: v.domain.trim() || null,
      timezone: v.timezone,
      currency: v.currency,
    }),
    onSuccess: (created) => {
      toast.success('Заведение создано');
      qc.invalidateQueries({ queryKey: ['admin/ests'] });
      onCreated(created.id);
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Новое заведение"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn-primary"
            disabled={!v.name.trim() || mut.isPending}
            onClick={() => mut.mutate()}
          >
            Создать
          </button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label className="label">Название *</label>
          <input className="input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        </div>
        <div>
          <label className="label">Адрес</label>
          <AddressField
            value={v.address}
            city={v.city}
            hasCoords={v.geoLat != null && v.geoLon != null}
            onChange={({ address, city, lat, lon }) =>
              setV({ ...v, address, city, geoLat: lat, geoLon: lon })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Телефон</label>
            <input className="input" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} />
          </div>
          <div>
            <label className="label">Валюта</label>
            <input className="input" value={v.currency} onChange={(e) => setV({ ...v, currency: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Таймзона</label>
          <input className="input" value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })} />
        </div>
        <div>
          <label className="label">Домен меню (опционально)</label>
          <DomainField value={v.domain} onChange={(domain) => setV({ ...v, domain })} compact />
        </div>
        <div className="text-xs text-slate-500">
          После создания добавьте категории, блюда и позиции склада — у нового заведения всё пусто.
        </div>
      </div>
    </Modal>
  );
}

// ─── DomainField — поле с real-time-валидацией hostname ─────────────
// Та же regex-логика что и на бэке (RFC 1035): 1-63 на label, 2+ части,
// общая длина ≤ 253. Нормализуем введённое значение (https://, порт, путь, lowercase)
// чтобы пользователю не приходилось чистить вручную.

const HOSTNAME_RE =
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

function normalizeDomainClient(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.replace(/^\/+/, '');
  s = s.split('/')[0];
  s = s.split(':')[0];
  s = s.replace(/\.+$/, '');
  return s;
}

function DomainField({
  value,
  onChange,
  compact = false,
}: {
  value: string;
  onChange: (domain: string) => void;
  compact?: boolean;
}) {
  const normalized = normalizeDomainClient(value);
  const isEmpty = normalized.length === 0;
  const isValid = !isEmpty && HOSTNAME_RE.test(normalized);
  const isMutated = !isEmpty && normalized !== value.trim();

  return (
    <div>
      <div className="relative">
        <input
          className={`input pr-10 ${
            isEmpty
              ? ''
              : isValid
                ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                : 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
          }`}
          placeholder="menu.mycafe.ru"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            // На blur сразу нормализуем — убираем https://, лишние слеши и т.д.
            if (isMutated) onChange(normalized);
          }}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {!isEmpty && (
          <span
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
              isValid ? 'text-emerald-600' : 'text-rose-600'
            }`}
            aria-hidden
          >
            {isValid ? '✓' : '✕'}
          </span>
        )}
      </div>

      {!isEmpty && isMutated && (
        <div className="mt-1 text-xs text-amber-600">
          Будет сохранён как <code className="font-mono">{normalized}</code>
        </div>
      )}
      {!isEmpty && !isValid && !isMutated && (
        <div className="mt-1 text-xs text-rose-600">
          Некорректный домен. Пример: <code className="font-mono">menu.mycafe.ru</code>
        </div>
      )}

      {!compact && (
        <details className="group mt-3">
          <summary className="cursor-pointer select-none text-xs font-medium text-brand-600 hover:text-brand-700">
            Как настроить DNS на стороне домена
          </summary>
          <div className="mt-2 space-y-1.5 border-l-2 border-brand-200 pl-3 text-xs text-slate-500">
            <p>В панели регистратора (REG.RU, Namecheap, Cloudflare) добавьте одну запись:</p>
            <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-[11px]">
              <div><span className="text-slate-400">тип:</span> <strong>A</strong></div>
              <div><span className="text-slate-400">имя:</span> <strong>menu</strong> (или ваш subdomain)</div>
              <div><span className="text-slate-400">значение:</span> <strong>93.77.167.70</strong></div>
              <div><span className="text-slate-400">TTL:</span> 300 (или авто)</div>
            </div>
            <p>
              Альтернатива: <strong>CNAME</strong> на <code className="font-mono">menu.resstro.ru</code>.
              DNS обновится за 5–15 минут, сертификат выдастся при первом запросе.
            </p>
          </div>
        </details>
      )}
    </div>
  );
}
